import { useEffect, useRef, useState, type JSX } from 'react';
import { useAuthStore } from './features/auth/authStore';
import { makeApi } from './lib/api';
import { LoginForm } from './features/auth/LoginForm';
import { RegisterForm } from './features/auth/RegisterForm';
import TokenPasteShell from './features/auth/TokenPasteShell';
import { CreateFirstSpaceForm } from './features/auth/CreateFirstSpaceForm';
import EditorShell from './features/editor/EditorShell';

type UnauthMode = 'login' | 'register' | 'token';

/**
 * Peacock Admin — root component.
 *
 * Phase-3 flow:
 *   1. No token → LoginForm (default) / RegisterForm / TokenPasteShell
 *   2. Token + user has 0 spaces → CreateFirstSpaceForm
 *   3. Token + user has ≥1 space → EditorShell
 *
 * On reload with a persisted token but no in-memory identity, we hit
 * `/v1/auth/me` first. If that fails, fall back to `/v1/whoami` for
 * Space-tokenable (developer) tokens.
 *
 * **Hydration robustness** — the effect uses a token-keyed ref guard
 * so we never re-fetch identity for the same token, and we never
 * cancel the fetch via dep-array thrash (`user` going null → set
 * would otherwise tear down the in-flight `/me` before the result
 * could be applied — a latent bug found in production where
 * "Lade Profil…" would never resolve).
 */
export default function App(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const spaces = useAuthStore((s) => s.spaces);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const setUserAndSpaces = useAuthStore((s) => s.setUserAndSpaces);
  const setIdentity = useAuthStore((s) => s.setIdentity);
  const signOut = useAuthStore((s) => s.signOut);

  const [unauthMode, setUnauthMode] = useState<UnauthMode>('login');
  const [hydrationDone, setHydrationDone] = useState(false);
  const hydratedForToken = useRef<string | null>(null);

  useEffect(() => {
    if (token === null) {
      hydratedForToken.current = null;
      setHydrationDone(true);
      return;
    }
    // Only trust the in-memory `user` if it was hydrated for the
    // current token. After sign-out → sign-in-as-different-user the
    // store may briefly show a populated `user` paired with the NEW
    // token if Zustand-set hasn't flushed across the boundary — the
    // ref-match check guarantees we re-fetch `/me` in that case.
    if (user !== null && hydratedForToken.current === token) {
      setHydrationDone(true);
      return;
    }
    // Same token already being hydrated by a previous effect run —
    // don't kick off a parallel fetch.
    if (hydratedForToken.current === token) {
      return;
    }
    hydratedForToken.current = token;
    void (async () => {
      const api = makeApi(token);
      // Try the User-token endpoint first (email+password flow).
      const meResult = await api.me();
      if (meResult.status === 'ok') {
        setUserAndSpaces(meResult.data.user, meResult.data.spaces);
        setHydrationDone(true);
        return;
      }
      // Fall back to Space-tokenable endpoint for developers who
      // pasted a token via the TokenPasteShell.
      const who = await api.whoami();
      if (who.status === 'ok') {
        setIdentity(who.data.space_slug, who.data.abilities);
        setHydrationDone(true);
        return;
      }
      // 401 on /me means the token is dead (revoked, expired). Both
      // the SDK's `request` helper returns `{status:'unauthenticated'}`
      // and Laravel returns a 401 body — handle either shape.
      if (
        meResult.status === 'unauthenticated' ||
        (meResult.status === 'error' && meResult.code === 401)
      ) {
        signOut();
      }
      setHydrationDone(true);
    })();
    // `user` IS in the dep array so a login-path setSession (which
    // populates user + spaces without going through /me) triggers a
    // re-evaluation — the ref-match check on the next run will
    // recognize "this user matches this token" and short-circuit.
    // We deliberately do not cancel the in-flight async on dep change;
    // the ref guard makes parallel fetches impossible, so React-19
    // strict-mode double-mounts cost at most one extra /me round-trip.
  }, [token, user, setUserAndSpaces, setIdentity, signOut]);

  // ─── Not authenticated ───────────────────────────────────────────
  if (token === null) {
    if (unauthMode === 'register') {
      return <RegisterForm onSwitchToLogin={() => setUnauthMode('login')} />;
    }
    if (unauthMode === 'token') {
      return (
        <TokenPasteShell
          onSwitchToSignup={() => setUnauthMode('register')}
          onSwitchToLogin={() => setUnauthMode('login')}
        />
      );
    }
    return (
      <LoginForm
        onSwitchToSignup={() => setUnauthMode('register')}
        onSwitchToTokenPaste={() => setUnauthMode('token')}
      />
    );
  }

  // ─── Hydrating identity after reload ─────────────────────────────
  if (!hydrationDone) {
    return (
      <main className="grid min-h-screen place-items-center bg-zinc-50">
        <p className="text-sm text-zinc-600">Lade Profil…</p>
      </main>
    );
  }

  // ─── Authenticated user with no spaces yet ───────────────────────
  // Only fires for real-user accounts. Token-pasted dev sessions
  // always have a spaceSlug because the Sanctum token IS the Space.
  if (user !== null && spaces.length === 0) {
    return <CreateFirstSpaceForm />;
  }

  // ─── Authenticated but no current space selected ─────────────────
  if (spaceSlug === null) {
    return (
      <main className="grid min-h-screen place-items-center bg-zinc-50">
        <p className="text-sm text-zinc-600">Kein Space ausgewählt.</p>
      </main>
    );
  }

  return <EditorShell />;
}
